// src/pages/guides/EOLSoftwareGuide.js
// EOL Software Remediation Guide - SEO Playbook Page

import React from 'react';
import useTrialCta from '../../hooks/useTrialCta';
import GuideByline from './GuideByline';
import GuideAnswer from './GuideAnswer';
import GuideFAQ, { buildFAQPageSchema } from './GuideFAQ';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/common/SEOHead';
import { PublicHeader, PublicFooter } from '../../components/common';
import './Guides.css';

function EOLSoftwareGuide() {
  const startTrial = useTrialCta('guide_trial:eol-software');

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "End-of-Life Software Guide",
    "description": "How to handle end-of-life software on internet-facing systems. Why EOL equals permanent vulnerability, remediation strategies, and compensating controls.",
    "image": ["https://www.driftalarm.com/images/og-image.png"],
    "datePublished": "2025-12-01T00:00:00-05:00",
    "dateModified": "2026-08-25T00:00:00-04:00",
    "author": { "@type": "Organization", "name": "DriftAlarm", "url": "https://www.driftalarm.com" },
    "publisher": { "@type": "Organization", "name": "DriftAlarm", "url": "https://www.driftalarm.com", "logo": { "@type": "ImageObject", "url": "https://www.driftalarm.com/images/og-image.png" } },
    "url": "https://www.driftalarm.com/guides/eol-software",
    "mainEntityOfPage": "https://www.driftalarm.com/guides/eol-software"
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.driftalarm.com" },
      { "@type": "ListItem", "position": 2, "name": "Security Guides", "item": "https://www.driftalarm.com/guides" },
      { "@type": "ListItem", "position": 3, "name": "End-of-Life Software" }
    ]
  };

  return (
    <div className="guide-container">
      <SEOHead
        title="End-of-Life Software Guide | DriftAlarm"
        description="How to handle end-of-life software on internet-facing systems. Why EOL equals permanent vulnerability, remediation strategies, and compensating controls when upgrades are delayed."
        path="/guides/eol-software"
        ogType="article"
        jsonLd={[articleSchema, breadcrumbSchema, buildFAQPageSchema('/guides/eol-software')].filter(Boolean)}
      />
      <PublicHeader activeLink="guides" />

      <main className="guide-content">
        <section className="guide-hero">
          <h1>EOL Software: When No Patch Is Coming</h1>
          <GuideByline path="/guides/eol-software" />
          <GuideAnswer path="/guides/eol-software" />
          <p className="guide-hero-intro">
            End-of-life software on the internet is a permanent vulnerability. There will never be
            another security patch. Attackers know this and specifically target EOL systems because
            exploitation is guaranteed to work forever.
          </p>

          <div className="guide-hero-bullets">
            <div className="guide-hero-bullets-title">What You'll Learn</div>
            <ul>
              <li>Why EOL exposure is automatically P1</li>
              <li>How attackers target EOL systems</li>
              <li>Remediation strategies (upgrade, migrate, isolate)</li>
              <li>Compensating controls when immediate upgrade isn't possible</li>
              <li>Building an EOL inventory and tracking program</li>
            </ul>
          </div>

          <div className="guide-cta-group">
            <button className="da-btn da-btn--primary" onClick={startTrial}>
              <span>Start Free 30-Day Trial</span>
              <span>→</span>
            </button>
            <Link to="/guides/attack-surface-monitoring" className="da-btn da-btn--secondary">
              Back to Attack Surface Monitoring Guide
            </Link>
          </div>
        </section>

        <nav className="guide-toc">
          <div className="guide-toc-title">In This Guide</div>
          <ul className="guide-toc-list">
            <li><a href="#why-critical">Why EOL Is Permanently Critical</a></li>
            <li><a href="#attacker-perspective">The Attacker Perspective</a></li>
            <li><a href="#common-eol">Common EOL Software</a></li>
            <li><a href="#remediation">Remediation Strategies</a></li>
            <li><a href="#compensating">Compensating Controls</a></li>
            <li><a href="#tracking">EOL Tracking Program</a></li>
            <li><a href="#faq">Frequently Asked Questions</a></li>
          </ul>
        </nav>

        <section id="why-critical" className="guide-section">
          <h2>Why EOL Is Permanently Critical</h2>

          <div className="callout callout-critical">
            <div className="callout-title">Instant P1: EOL + Internet Exposure</div>
            <div className="callout-content">
              <p>
                <strong>End-of-life software exposed to the internet without strong compensating
                controls is an automatic P1.</strong> This override applies regardless of what the
                software is—web servers, databases, operating systems, or applications.
              </p>
            </div>
          </div>

          <p>
            End-of-life means the vendor has stopped providing security updates. This creates a
            fundamentally different risk profile than supported software:
          </p>

          <div className="guide-table-wrapper">
            <table className="guide-table">
              <thead>
                <tr>
                  <th>Supported Software</th>
                  <th>EOL Software</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>New vulnerabilities get patched</td>
                  <td>Vulnerabilities accumulate forever</td>
                </tr>
                <tr>
                  <td>Patch within 30-90 days = managed risk</td>
                  <td>No patch available at any timeline</td>
                </tr>
                <tr>
                  <td>Attackers must find 0-days</td>
                  <td>Every known CVE is a permanent 0-day</td>
                </tr>
                <tr>
                  <td>Risk decreases over time with patching</td>
                  <td>Risk only increases as more vulns discovered</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="callout callout-warning">
            <div className="callout-title">Attackers Actively Scan for EOL Systems</div>
            <div className="callout-content">
              <p>
                <strong>Shodan, Censys, and attacker-operated scanners specifically fingerprint for
                EOL software.</strong> When attackers find an EOL system, they know:
              </p>
              <ul>
                <li>Public exploits will work—there's no patch</li>
                <li>The organization likely has weak security practices</li>
                <li>Compromise is almost guaranteed with minimal effort</li>
              </ul>
            </div>
          </div>
        </section>

        <section id="attacker-perspective" className="guide-section">
          <h2>The Attacker Perspective</h2>

          <h3>Why Attackers Love EOL Targets</h3>
          <ul>
            <li><strong>Guaranteed exploitation:</strong> Known CVEs will work forever. No race against patches.</li>
            <li><strong>Exploit kits ready:</strong> Popular EOL software has exploit code readily available</li>
            <li><strong>Easy discovery:</strong> Version fingerprinting reveals EOL status immediately</li>
            <li><strong>Signal of weak security:</strong> If they run EOL externally, internal security is likely weak</li>
            <li><strong>Low attacker effort:</strong> No 0-day development needed, just use public exploits</li>
          </ul>

          <h3>Attack Timeline</h3>
          <p>
            Attacks against EOL systems are often trivial:
          </p>
          <ol>
            <li><strong>Scan (seconds):</strong> Fingerprint software version from banner or response</li>
            <li><strong>Identify (seconds):</strong> Check if version is EOL</li>
            <li><strong>Exploit (minutes):</strong> Use known CVE with public exploit code</li>
            <li><strong>Compromise (minutes):</strong> Gain shell access or data</li>
          </ol>

          <div className="example-box">
            <div className="example-title">Real Example: Windows Server 2008 R2</div>
            <div className="example-content">
              <p>
                Windows Server 2008 R2 reached end of life in January 2020. Since then:
              </p>
              <ul>
                <li>EternalBlue (MS17-010) remains permanently exploitable</li>
                <li>ZeroLogon (CVE-2020-1472) has no patch for 2008 R2</li>
                <li>PrintNightmare and other vulns accumulate with no fixes</li>
              </ul>
              <p>
                Any 2008 R2 server exposed to the internet can typically be compromised quickly using
                freely available exploit tools.
              </p>
            </div>
          </div>
        </section>

        <section id="common-eol" className="guide-section">
          <h2>Commonly Found EOL Software</h2>

          <h3>Operating Systems</h3>
          <div className="guide-table-wrapper">
            <table className="guide-table">
              <thead>
                <tr>
                  <th>Software</th>
                  <th>EOL Date</th>
                  <th>Years Without Patches (as of August 2026)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Windows Server 2012/2012 R2</td>
                  <td>October 2023</td>
                  <td>2+ years</td>
                </tr>
                <tr>
                  <td>Windows Server 2008/2008 R2</td>
                  <td>January 2020</td>
                  <td>6+ years</td>
                </tr>
                <tr>
                  <td>Ubuntu 18.04 LTS</td>
                  <td>May 2023 (standard)</td>
                  <td>3+ years</td>
                </tr>
                <tr>
                  <td>CentOS 7</td>
                  <td>June 2024</td>
                  <td>2+ years</td>
                </tr>
                <tr>
                  <td>CentOS 8</td>
                  <td>December 2021</td>
                  <td>4+ years</td>
                </tr>
                <tr>
                  <td>Debian 10 (Buster)</td>
                  <td>June 2024</td>
                  <td>2+ years</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3>Web Servers & Middleware</h3>
          <ul>
            <li><strong>Apache 2.2:</strong> EOL December 2017</li>
            <li><strong>nginx 1.18 and earlier:</strong> Various EOL dates, check specific version</li>
            <li><strong>PHP 7.4:</strong> EOL November 2022</li>
            <li><strong>PHP 8.0:</strong> EOL November 2023</li>
            <li><strong>PHP 8.1:</strong> EOL December 2025</li>
            <li><strong>Tomcat 8.5:</strong> EOL March 2024</li>
            <li><strong>Node.js LTS versions:</strong> Check nodejs.org for current EOL schedule</li>
          </ul>

          <h3>Databases</h3>
          <ul>
            <li><strong>MySQL 5.7:</strong> EOL October 2023</li>
            <li><strong>PostgreSQL 11:</strong> EOL November 2023</li>
            <li><strong>PostgreSQL 12:</strong> EOL November 2024</li>
            <li><strong>SQL Server 2012:</strong> Extended support ended July 2022</li>
            <li><strong>MongoDB 4.x:</strong> Various EOL dates by minor version</li>
          </ul>

          <div className="callout callout-info">
            <div className="callout-title">Check Vendor EOL Schedules</div>
            <div className="callout-content">
              <p>
                Always verify EOL dates from official vendor sources. Major vendors publish EOL schedules:
              </p>
              <ul>
                <li>Microsoft: learn.microsoft.com/lifecycle</li>
                <li>Ubuntu: ubuntu.com/about/release-cycle</li>
                <li>Red Hat/CentOS: access.redhat.com/product-life-cycles</li>
                <li>PostgreSQL: postgresql.org/support/versioning</li>
              </ul>
            </div>
          </div>
        </section>

        <section id="remediation" className="guide-section">
          <h2>Remediation Strategies</h2>

          <h3>Option 1: Upgrade (Preferred)</h3>
          <p>
            Upgrade to a supported version of the same software:
          </p>
          <ul>
            <li><strong>Best outcome:</strong> Minimal architectural change, continued vendor support</li>
            <li><strong>Timeline:</strong> Plan for 1-4 weeks depending on complexity</li>
            <li><strong>Risk:</strong> Application compatibility, testing requirements</li>
          </ul>
          <p>
            Upgrade should be the default approach unless technical constraints prevent it.
          </p>

          <h3>Option 2: Migrate to Alternative</h3>
          <p>
            Move to a different but supported platform:
          </p>
          <ul>
            <li>CentOS → Rocky Linux, AlmaLinux, or RHEL</li>
            <li>Windows Server 2008 → Windows Server 2025 or Linux</li>
            <li>Legacy application → Modern equivalent or SaaS</li>
          </ul>
          <p>
            Migration is appropriate when the EOL software has no supported upgrade path.
          </p>

          <h3>Option 3: Replace with Modern Architecture</h3>
          <p>
            Sometimes EOL systems indicate technical debt that should be addressed:
          </p>
          <ul>
            <li>Containerize applications for easier updates</li>
            <li>Move to managed cloud services (RDS, Azure SQL, etc.)</li>
            <li>Rewrite legacy applications</li>
          </ul>
          <p>
            Longer timeline but addresses root cause of EOL accumulation.
          </p>

          <h3>Option 4: Decommission</h3>
          <p>
            Sometimes the right answer is to turn it off:
          </p>
          <ul>
            <li>Is this system still needed?</li>
            <li>Can its function be absorbed by other systems?</li>
            <li>Is the business value worth the security risk?</li>
          </ul>

          <div className="callout callout-warning">
            <div className="callout-title">Extended Support Is Not a Long-Term Solution</div>
            <div className="callout-content">
              <p>
                Vendors sometimes offer paid extended support (Microsoft ESU, Ubuntu Pro). This buys
                time but:
              </p>
              <ul>
                <li>Extended support is expensive and still has an end date</li>
                <li>Not all vulnerabilities are patched in extended support</li>
                <li>Use extended support to bridge to a real remediation, not as permanent solution</li>
              </ul>
            </div>
          </div>
        </section>

        <section id="compensating" className="guide-section">
          <h2>Compensating Controls</h2>
          <p>
            When immediate remediation isn't possible, implement compensating controls to reduce risk.
            <strong>These do not eliminate the risk—they reduce it while you plan remediation.</strong>
          </p>

          <h3>Network Isolation</h3>
          <ul>
            <li><strong>Remove from internet:</strong> If internet exposure isn't required, block it</li>
            <li><strong>IP allowlisting:</strong> Restrict access to specific known IPs</li>
            <li><strong>Network segmentation:</strong> Isolate EOL systems from critical assets</li>
            <li><strong>VPN requirement:</strong> Require VPN to access the system</li>
          </ul>

          <h3>Web Application Firewall (WAF)</h3>
          <ul>
            <li>Deploy WAF in front of EOL web servers</li>
            <li>Enable virtual patching rules for known CVEs</li>
            <li>Block common exploit patterns</li>
            <li>Rate limit and bot protection</li>
          </ul>

          <h3>Enhanced Monitoring</h3>
          <ul>
            <li>Increase logging and log retention</li>
            <li>Deploy IDS/IPS with signatures for known exploits</li>
            <li>Alert on any unusual activity</li>
            <li>Consider deploying honeypot alongside to detect targeting</li>
          </ul>

          <h3>Access Restrictions</h3>
          <ul>
            <li>Implement MFA for all access (even if software doesn't support it, put MFA proxy in front)</li>
            <li>Reduce administrative access to minimum necessary</li>
            <li>Audit and remove unnecessary services/ports</li>
          </ul>

          <div className="callout callout-critical">
            <div className="callout-title">Compensating Controls Have Limits</div>
            <div className="callout-content">
              <p>
                <strong>Compensating controls reduce risk but don't eliminate it.</strong> A WAF can
                block known exploit patterns, but not zero-days or novel techniques. Network isolation
                helps but doesn't protect against authorized users who are compromised.
              </p>
              <p>
                <strong>Compensating controls must have an expiration date.</strong> Document when
                remediation will complete and hold the organization accountable.
              </p>
            </div>
          </div>
        </section>

        <section id="tracking" className="guide-section">
          <h2>Building an EOL Tracking Program</h2>

          <h3>Inventory Requirements</h3>
          <p>Maintain an inventory that tracks:</p>
          <ul>
            <li>All software versions in production (OS, middleware, databases, applications)</li>
            <li>EOL dates for each software component</li>
            <li>Internet exposure status</li>
            <li>Business criticality</li>
            <li>Remediation plan and timeline</li>
          </ul>

          <h3>Proactive Planning</h3>
          <ul>
            <li><strong>12-month warning:</strong> Begin planning for EOL software 12 months out</li>
            <li><strong>6-month warning:</strong> Remediation project should be approved and funded</li>
            <li><strong>3-month warning:</strong> Implementation should be in progress</li>
            <li><strong>EOL date:</strong> No internet-facing EOL without approved exception</li>
          </ul>

          <h3>Exception Process</h3>
          <p>
            When EOL systems must remain temporarily:
          </p>
          <ul>
            <li>Document business justification</li>
            <li>List all compensating controls implemented</li>
            <li>Set hard expiration date (no indefinite exceptions)</li>
            <li>Require executive sign-off accepting residual risk</li>
            <li>Review monthly until remediated</li>
          </ul>

          <h3>Metrics to Track</h3>
          <ul>
            <li>Number of EOL systems (total and internet-facing)</li>
            <li>Average age past EOL date</li>
            <li>Time to remediate once EOL detected</li>
            <li>Exception count and average exception duration</li>
          </ul>
        </section>

        {/* PRD-193 WP4: this guide previously had NO link to /upgrade or
            /how-it-works -- its only exit was a trial button that went to the sign-IN
            form. Mid-article conversion path, matching the block the other guides use. */}
        <section className="guide-section">
          <div className="guide-cta-section">
            <h3>How DriftAlarm Helps</h3>
            <p>Software ages out on a schedule nobody watches. Continuous external scanning fingerprints the versions your perimeter actually advertises and tracks them against end-of-life dates, so a component that crosses that line is flagged on the next cycle rather than discovered during a renewal review.</p>
            <div className="guide-cta-buttons">
              <button onClick={startTrial} className="da-btn da-btn--primary">Start Free 30-Day Trial</button>
              <Link to="/how-it-works" className="da-btn da-btn--secondary">See How It Works</Link>
            </div>
          </div>
        </section>

        <section className="guide-section">
          <h2>Related Security Guides</h2>
          <p>
            EOL software is often found alongside other attack surface issues. Review these related guides:
          </p>
          <ul>
            <li>
              <Link to="/guides/php2shell-cve-2026-109618-109619">PHP2Shell Advisory (CVE-2026-109618 / CVE-2026-109619)</Link> — PHP 8.1 and older received no packaged fix for these two remote code execution flaws.
            </li>
            <li>
              <Link to="/guides/exposed-rdp">Exposed RDP Guide</Link> — EOL Windows servers often have exposed RDP, creating a doubly dangerous combination.
            </li>
            <li>
              <Link to="/guides/exposed-ssh">Exposed SSH Guide</Link> — Outdated SSH daemon versions (OpenSSH, Dropbear) on EOL operating systems compound risk significantly.
            </li>
            <li>
              <Link to="/guides/exposed-admin-panels">Exposed Admin Panels Guide</Link> — EOL CMS platforms and management interfaces require immediate attention.
            </li>
            <li>
              <Link to="/guides/attack-surface-monitoring">Attack Surface Monitoring Guide</Link> — Learn how to continuously monitor for EOL software appearing on your perimeter before attackers discover it.
            </li>
          </ul>
        </section>

        <section className="guide-section">
          <h2>Summary: EOL Response Checklist</h2>
          <div className="quickstart-list">
            <div className="quickstart-day">
              <div className="quickstart-day-header">
                <span className="quickstart-day-label">Immediate</span>
                <span className="quickstart-day-title">When EOL System Discovered</span>
              </div>
              <ul className="quickstart-day-tasks">
                <li>Confirm EOL status with vendor lifecycle data</li>
                <li>Assess internet exposure (direct, indirect, internal-only)</li>
                <li>If internet-facing: classify as P1</li>
                <li>Notify system owner and security leadership</li>
              </ul>
            </div>

            <div className="quickstart-day">
              <div className="quickstart-day-header">
                <span className="quickstart-day-label">72 Hours</span>
                <span className="quickstart-day-title">Initial Response</span>
              </div>
              <ul className="quickstart-day-tasks">
                <li>Implement compensating controls (network, WAF, monitoring)</li>
                <li>Document business justification if can't remediate immediately</li>
                <li>Create remediation plan with timeline</li>
                <li>Get executive approval for any exception</li>
              </ul>
            </div>

            <div className="quickstart-day">
              <div className="quickstart-day-header">
                <span className="quickstart-day-label">30 Days</span>
                <span className="quickstart-day-title">Remediation</span>
              </div>
              <ul className="quickstart-day-tasks">
                <li>Complete upgrade, migration, or decommission</li>
                <li>Verify remediation (version check, vulnerability scan)</li>
                <li>Remove compensating controls if no longer needed</li>
                <li>Update inventory and close finding</li>
              </ul>
            </div>
          </div>

          <div className="guide-cta-group" style={{ marginTop: '40px' }}>
            <button className="da-btn da-btn--primary" onClick={startTrial}>
              <span>Start Free 30-Day Trial</span>
              <span>→</span>
            </button>
          </div>
        </section>
        <GuideFAQ path="/guides/eol-software" />
      </main>

      <PublicFooter />
    </div>
  );
}

export default EOLSoftwareGuide;
